import { customElement, scss } from '../../core/ids-decorators';
import { attributes } from '../../core/ids-attributes';
import { stringToBool, stringToNumber } from '../../utils/ids-string-utils/ids-string-utils';

import IdsElement from '../../core/ids-element';

import styles from './ids-layout-flex.scss';

import './ids-layout-flex-item';

// List of flex options
export const FLEX_OPTIONS = {
  alignContent: ['flex-start', 'flex-end', 'center', 'space-between', 'space-around', 'space-evenly', 'stretch', 'start', 'end', 'baseline'],
  alignItems: ['start', 'end', 'flex-start', 'flex-end', 'center', 'center-inputs', 'baseline', 'stretch'],
  direction: ['row', 'row-reverse', 'column', 'column-reverse'],
  display: ['flex', 'inline-flex'],
  justifyContent: ['start', 'stretch', 'end', 'flex-start', 'flex-end', 'center', 'left', 'right', 'space-between', 'space-around', 'space-evenly'],
  wrap: ['nowrap', 'wrap', 'wrap-reverse'],
  units: [0, 1, 2, 4, 8, 12, 16, 20, 24, 28, 32, 36, 40],
  // Named spacing tokens mirror ids-layout-grid so the two components are interchangeable.
  gapSizes: ['none', 'xs', 'sm', 'md', 'lg', 'xl'],
  paddingSizes: ['xxs', 'xs', 'sm', 'md', 'lg', 'xl'],
  marginSizes: ['xs', 'sm', 'md', 'lg', 'xl', 'auto']
};

// Shared, page-wide batch for button-wrap detection. When many center-inputs
// containers resize in the same frame — e.g. a form-heavy page inside an ExtJS
// layout pass that resizes the whole region — reacting per-instance interleaves
// reads (getBoundingClientRect) with writes (class toggle), forcing one reflow
// per container: O(N) reflows per frame. Batching splits the frame into a single
// read pass across all pending containers, then a single write pass, so the whole
// page costs one reflow per frame no matter how many containers are pending.
const wrapBatch = new Set<IdsLayoutFlex>();
let wrapBatchRaf = 0;

const flushWrapBatch = () => {
  wrapBatchRaf = 0;
  const pending = [...wrapBatch];
  wrapBatch.clear();
  // Read phase: measure everything first so no write has dirtied layout yet.
  const results = pending.map((el) => el.measureButtonWrap());
  // Write phase: apply the class toggles after all reads are done.
  pending.forEach((el, i) => el.applyButtonWrap(results[i]));
};

const scheduleWrapBatch = (el: IdsLayoutFlex) => {
  wrapBatch.add(el);
  if (!wrapBatchRaf) wrapBatchRaf = requestAnimationFrame(flushWrapBatch);
};

/**
 * IDS Layout Flex Component
 * @type {IdsLayoutFlex}
 * @inherits IdsElement
 */
@customElement('ids-layout-flex')
@scss(styles)
export default class IdsLayoutFlex extends IdsElement {
  constructor() {
    super();
  }

  static get attributes(): any {
    return [
      ...super.attributes,
      attributes.ALIGN_CONTENT,
      attributes.ALIGN_ITEMS,
      attributes.DIRECTION,
      attributes.DISPLAY,
      attributes.FILL,
      attributes.GAP,
      attributes.GAP_X,
      attributes.GAP_Y,
      attributes.HEIGHT,
      attributes.JUSTIFY_CONTENT,
      attributes.MARGIN,
      attributes.MARGIN_Y,
      attributes.PADDING,
      attributes.PADDING_X,
      attributes.PADDING_Y,
      attributes.WRAP
    ];
  }

  #childObserver?: MutationObserver;

  #resizeObserver?: ResizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#syncChildFieldHeight();
    this.#syncButtonWrap();
    this.#observeChildren();
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    this.#childObserver?.disconnect();
    this.#childObserver = undefined;
    this.#disconnectResize();
    wrapBatch.delete(this);
  }

  /**
   * For align-items='center-inputs': inspect descendant inputs for their
   * field-height / compact setting and reflect it onto this host so the
   * ids-checkbox can size itself to match via :host-context selectors.
   * Also flags whether any descendant field renders its label on top, which
   * gates the button offset (see the has-button rule in the SCSS).
   */
  #syncChildFieldHeight(): void {
    const current = this.getAttribute(attributes.FIELD_HEIGHT);

    if (this.alignItems !== 'center-inputs') {
      if (current !== null) this.removeAttribute(attributes.FIELD_HEIGHT);
      this.classList.remove('has-top-label-field');
      return;
    }

    const inputs = this.querySelectorAll<HTMLElement>(
      'ids-input, ids-date-picker, ids-time-picker, ids-dropdown, ids-textarea, ids-trigger-field'
    );
    let detected: string | null = null;
    // A top label sits above the field, so a sibling button must be pushed down
    // to reach the field row. An inline-start label sits beside the field, so the
    // field already starts at the top and no offset is needed. `top` is the
    // default when no label-position is set.
    let hasTopLabel = false;
    inputs.forEach((el) => {
      const labelPosition = el.getAttribute('label-position');
      if (el.hasAttribute('label') && labelPosition !== 'inline-start') {
        hasTopLabel = true;
      }
      if (detected) return;
      const fh = el.getAttribute(attributes.FIELD_HEIGHT);
      if (fh) {
        detected = fh;
      } else if (el.hasAttribute(attributes.COMPACT)) {
        detected = 'xs';
      }
    });

    this.classList.toggle('has-top-label-field', hasTopLabel);

    if (detected) {
      if (current !== detected) this.setAttribute(attributes.FIELD_HEIGHT, detected);
    } else if (current !== null) {
      this.removeAttribute(attributes.FIELD_HEIGHT);
    }
  }

  /**
   * Turn wrap detection on/off for this container and queue a (batched) remeasure.
   * The measurement itself is deferred so it never forces a reflow synchronously
   * during connect, a mutation batch, or a resize notification.
   */
  #syncButtonWrap(): void {
    // The wrap offset is a center-inputs-only concern, so only these containers
    // pay for a ResizeObserver — every other ids-layout-flex on the page skips it.
    if (this.alignItems !== 'center-inputs') {
      this.classList.remove('has-wrapped-button');
      this.#disconnectResize();
      wrapBatch.delete(this);
      return;
    }
    this.#observeResize();
    scheduleWrapBatch(this);
  }

  /**
   * Read phase (called by the shared batch): measure whether the button has
   * wrapped. Compare against the preceding sibling horizontally, not vertically:
   * the button carries a top-offset margin while on the field row, so its top can
   * sit below the sibling even when unwrapped — a vertical check would flag a
   * false positive and remove its own trigger. On the same row the button's left
   * edge sits to the right of the sibling; once wrapped it starts back at the
   * container's left, so its left edge is no longer past the sibling's.
   * @returns {boolean} whether the button item has wrapped onto its own line
   */
  measureButtonWrap(): boolean {
    if (this.alignItems !== 'center-inputs') return false;
    const items = [...this.querySelectorAll<HTMLElement>(':scope > ids-layout-flex-item')];
    const buttonIndex = items.findIndex((el) => el.classList.contains('has-button'));
    const button = items[buttonIndex];
    const prev = items[buttonIndex - 1];
    if (!button || !prev) return false;
    const buttonBox = button.getBoundingClientRect();
    const prevBox = prev.getBoundingClientRect();
    return buttonBox.left <= prevBox.left;
  }

  /**
   * Write phase (called by the shared batch): apply the measured wrap state.
   * @param {boolean} wrapped result of measureButtonWrap
   */
  applyButtonWrap(wrapped: boolean): void {
    this.classList.toggle('has-wrapped-button', wrapped);
  }

  #observeResize(): void {
    if (this.#resizeObserver) return;
    // Route resize notifications through the shared batch so a page-wide resize
    // (many containers at once) costs one reflow per frame, not one per container.
    this.#resizeObserver = new ResizeObserver(() => scheduleWrapBatch(this));
    this.#resizeObserver.observe(this);
  }

  #disconnectResize(): void {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
  }

  #observeChildren(): void {
    this.#childObserver = new MutationObserver((records) => {
      // Ignore mutations on the host itself to avoid feedback loops when
      // we reflect the inferred field-height back onto this element.
      const fromChild = records.some((r) => r.target !== this);
      if (fromChild) {
        this.#syncChildFieldHeight();
        this.#syncButtonWrap();
      }
    });
    this.#childObserver.observe(this, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: [attributes.FIELD_HEIGHT, attributes.COMPACT, attributes.LABEL_POSITION]
    });
  }

  template(): string {
    return `<slot></slot>`;
  }

  /**
   * Set the align content setting
   * @param {string} value The value
   */
  set alignContent(value: string | null) {
    if (value && FLEX_OPTIONS.alignContent.includes(value)) {
      this.setAttribute(attributes.ALIGN_CONTENT, value);
    } else {
      this.removeAttribute(attributes.ALIGN_CONTENT);
    }
  }

  get alignContent() { return this.getAttribute(attributes.ALIGN_CONTENT); }

  /**
   * Set the align items setting
   * @param {string} value The value
   */
  set alignItems(value: string | null) {
    if (value && FLEX_OPTIONS.alignItems.includes(value)) {
      this.setAttribute(attributes.ALIGN_ITEMS, value);
    } else {
      this.removeAttribute(attributes.ALIGN_ITEMS);
    }
    this.#syncChildFieldHeight();
    // Starts the resize observer when switching to center-inputs, stops it otherwise.
    this.#syncButtonWrap();
  }

  get alignItems() { return this.getAttribute(attributes.ALIGN_ITEMS); }

  /**
   * Set the direction setting
   * @param {string} value The value
   */
  set direction(value: string | null) {
    if (value && FLEX_OPTIONS.direction.includes(value)) {
      this.setAttribute(attributes.DIRECTION, value);
    } else {
      this.removeAttribute(attributes.DIRECTION);
    }
  }

  get direction() { return this.getAttribute(attributes.DIRECTION); }

  /**
   * Set the display setting
   * @param {string} value The value
   */
  set display(value: string | null) {
    if (value && FLEX_OPTIONS.display.includes(value)) {
      this.setAttribute(attributes.DISPLAY, value);
    } else {
      this.removeAttribute(attributes.DISPLAY);
    }
  }

  get display() { return this.getAttribute(attributes.DISPLAY); }

  /**
   * Validates a gap value (named token or pixel unit) and writes/removes the attribute.
   * @param {string} attr The attribute name
   * @param {number | string | null} value The gap value to validate and set
   */
  #setGapAttribute(attr: string, value: number | string | null): void {
    if (typeof value === 'string' && FLEX_OPTIONS.gapSizes.includes(value)) {
      this.setAttribute(attr, value);
      return;
    }
    const val = stringToNumber(value);
    if (!Number.isNaN(val) && FLEX_OPTIONS.units.includes(val)) {
      this.setAttribute(attr, String(val));
    } else {
      this.removeAttribute(attr);
    }
  }

  /**
   * Set the gap, apply same for both horizontal and vertical sides
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gap(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP, value);
  }

  get gap() { return this.getAttribute(attributes.GAP); }

  /**
   * Set the horizontal gap
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gapX(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP_X, value);
  }

  get gapX() { return this.getAttribute(attributes.GAP_X); }

  /**
   * Set card vertical gap
   * @param {number|string} value Named token ('none', 'xs', 'sm', 'md', 'lg', 'xl') or pixel unit
   */
  set gapY(value: number | string | null) {
    this.#setGapAttribute(attributes.GAP_Y, value);
  }

  get gapY() { return this.getAttribute(attributes.GAP_Y); }

  /**
   * Set the padding (all sides). Accepts named tokens that mirror ids-layout-grid.
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set padding(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING, value);
    } else {
      this.removeAttribute(attributes.PADDING);
    }
  }

  get padding() { return this.getAttribute(attributes.PADDING); }

  /**
   * Set the horizontal padding (padding-inline).
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set paddingX(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING_X, value);
    } else {
      this.removeAttribute(attributes.PADDING_X);
    }
  }

  get paddingX() { return this.getAttribute(attributes.PADDING_X); }

  /**
   * Set the vertical padding (padding-block).
   * @param {string|null} value 'xxs', 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set paddingY(value: string | null) {
    if (value && FLEX_OPTIONS.paddingSizes.includes(value)) {
      this.setAttribute(attributes.PADDING_Y, value);
    } else {
      this.removeAttribute(attributes.PADDING_Y);
    }
  }

  get paddingY() { return this.getAttribute(attributes.PADDING_Y); }

  /**
   * Set the margin (all sides). Accepts named tokens that mirror ids-layout-grid.
   * @param {string|null} value 'xs', 'sm', 'md', 'lg', 'xl', 'auto', or null
   */
  set margin(value: string | null) {
    if (value && FLEX_OPTIONS.marginSizes.includes(value)) {
      this.setAttribute(attributes.MARGIN, value);
    } else {
      this.removeAttribute(attributes.MARGIN);
    }
  }

  get margin() { return this.getAttribute(attributes.MARGIN); }

  /**
   * Set the vertical margin (margin-block).
   * @param {string|null} value 'xs', 'sm', 'md', 'lg', 'xl', or null
   */
  set marginY(value: string | null) {
    if (value && FLEX_OPTIONS.marginSizes.includes(value) && value !== 'auto') {
      this.setAttribute(attributes.MARGIN_Y, value);
    } else {
      this.removeAttribute(attributes.MARGIN_Y);
    }
  }

  get marginY() { return this.getAttribute(attributes.MARGIN_Y); }

  /**
   * Set the height of the flex container
   * @param {string} value The height value (e.g.'200px')
   */
  set height(value: string | null) {
    if (value) {
      this.setAttribute(attributes.HEIGHT, value);
      this.style.height = value;
    } else {
      this.removeAttribute(attributes.HEIGHT);
      this.style.height = '';
    }
  }

  get height() { return this.getAttribute(attributes.HEIGHT); }

  /**
   * Set the justify content setting
   * @param {string} value The value
   */
  set justifyContent(value: string | null) {
    if (value && FLEX_OPTIONS.justifyContent.includes(value)) {
      this.setAttribute(attributes.JUSTIFY_CONTENT, value);
    } else {
      this.removeAttribute(attributes.JUSTIFY_CONTENT);
    }
  }

  get justifyContent() { return this.getAttribute(attributes.JUSTIFY_CONTENT); }

  /**
   * Set the wrap setting
   * @param {string} value The value
   */
  set wrap(value: string | null) {
    if (value && FLEX_OPTIONS.wrap.includes(value)) {
      this.setAttribute(attributes.WRAP, value);
    } else {
      this.removeAttribute(attributes.WRAP);
    }
  }

  get wrap() { return this.getAttribute(attributes.WRAP); }

  /**
   * Set flex container to 100% height
   * @param {boolean} val value
   */
  set fullHeight(val: boolean) {
    if (stringToBool(val)) {
      this.setAttribute(attributes.FULL_HEIGHT, '');
    } else {
      this.removeAttribute(attributes.FULL_HEIGHT);
    }
  }

  get fullHeight(): boolean {
    return this.hasAttribute(attributes.FULL_HEIGHT);
  }
}
